/**
 * KnockoutView — World Cup knockout bracket tab. Renders the static mirrored KnockoutBracket from
 * the real knockout fixtures on the matches feed (Round of 32 → Final, plus the third-place
 * play-off), or a provisional projection seeded from the groups in play before the draw lands.
 */

import React, { useMemo } from 'react'
import { View, ScrollView } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { useWorldCupPicksData } from '../../../src/worldcup/hooks/useWorldCupPicksData'
import { useWorldCupTournamentStandingsQuery } from '../../../src/worldcup/hooks/useWorldCupTournamentStandingsQuery'
import { buildGroupPositionMap } from '../../../src/worldcup/helpers/worldCupGroupStageStandings.helper'
import { WORLD_CUP_COMPETITION_ID } from '../../../src/worldcup/worldcup.constants'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { KnockoutBracket } from '../../knockout/KnockoutBracket'
import { WorldCupSkeleton } from '../WorldCupSkeleton/WorldCupSkeleton'
import {
  buildWorldCupBracketRounds,
  hasKnockoutFixtures,
} from '../../../src/worldcup/helpers/buildWorldCupBracket.helper'
import { computeKnockoutSlotsByKey } from '../../../src/worldcup/helpers/knockoutBracketOrder.helper'
import { buildProvisionalWorldCupBracket } from '../../../src/worldcup/helpers/buildProvisionalWorldCupBracket.helper'
import { mergeKnockoutBracket } from '../../../src/knockout/helpers/mergeKnockoutBracket.helper'
import { getKnockoutViewStyles } from './KnockoutView.styles'

export const KnockoutView: React.FC = () => {
  const theme = useTheme()
  const styles = useMemo(() => getKnockoutViewStyles(theme), [theme])

  const { matches, isLoading } = useWorldCupPicksData()
  const { isSignedIn, isLoaded } = useSupabaseAuth()
  // Standings give each knockout team's group + finishing position, used to anchor the Round of 32
  // to its bracket slots. React Query dedupes this with the picks hook's own standings fetch.
  const standingsQuery = useWorldCupTournamentStandingsQuery(
    WORLD_CUP_COMPETITION_ID,
    isLoaded && isSignedIn,
  )
  const groupPositionMap = useMemo(
    () => buildGroupPositionMap(standingsQuery.data?.groups ?? []),
    [standingsQuery.data?.groups],
  )

  // Build the real rounds (kickoff order) + third-place tie, then place each real tie into its
  // bracket slot. Slotting is per-match, so a half-drawn round yields slots for the known games
  // and `null` for the rest.
  const { realRounds, realThirdPlace } = useMemo(() => {
    const built = buildWorldCupBracketRounds(matches)
    return { realRounds: built.rounds, realThirdPlace: built.thirdPlace }
  }, [matches])
  const realSlotsByKey = useMemo(
    () => computeKnockoutSlotsByKey(realRounds, groupPositionMap),
    [realRounds, groupPositionMap],
  )
  const hasBracket = useMemo(() => hasKnockoutFixtures(matches), [matches])
  // Before the official knockout draw lands in the feed, project a provisional bracket seeded
  // from the groups actually in play (falls back to the official A–L letters).
  const provisionalModel = useMemo(() => {
    const letters = Array.from(
      new Set(matches.map((m) => m.groupLetter).filter((l): l is string => l != null && l !== '')),
    ).sort()
    return buildProvisionalWorldCupBracket(letters)
  }, [matches])

  // Overlay real ties onto the full provisional skeleton PER SLOT, so the tree always renders as a
  // complete R32 → Final bracket — real teams/results in every drawn slot (including a half-drawn
  // round, e.g. the first R16 games once their feeders finish) and "Winner of …" placeholders for
  // the rest — instead of collapsing to a lopsided partial tree or hiding known games.
  const model = useMemo(
    () => (hasBracket
      ? mergeKnockoutBracket(provisionalModel, realSlotsByKey, realThirdPlace)
      : provisionalModel),
    [hasBracket, provisionalModel, realSlotsByKey, realThirdPlace],
  )

  if (isLoading) {
    return (
      <View style={styles.container}>
        <WorldCupSkeleton variant="picks" />
      </View>
    )
  }

  // Note: a matches-load error intentionally falls through to the provisional bracket below —
  // it needs no network, so the user always sees a bracket rather than a dead error state.
  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      <KnockoutBracket model={model} testID="worldcup-knockout-bracket" />
    </ScrollView>
  )
}
